import React, { useState, useEffect } from 'react';
import { FileText, Printer, Download, Plus, Search } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import { formatArgentinaDate } from '../utils/dateUtils';
import { generateDeliveryNotePDF } from '../utils/pdfGenerator';
import DeliveryNoteModal from '../components/modals/DeliveryNoteModal';

const Remitos: React.FC = () => {
  const { clients } = useAppStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch delivery notes from local storage on component mount
  useEffect(() => {
    const fetchDeliveryNotes = () => {
      try {
        const storedNotes = localStorage.getItem('deliveryNotes');
        if (storedNotes) {
          setDeliveryNotes(JSON.parse(storedNotes));
        }
      } catch (error) {
        console.error('Error fetching delivery notes:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDeliveryNotes();
  }, []);
  
  // Filter delivery notes based on search query
  const filteredDeliveryNotes = deliveryNotes.filter(note => 
    note.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.carrierName.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Handle saving a new delivery note
  const handleSaveDeliveryNote = (deliveryNote: any) => {
    const newDeliveryNotes = [...deliveryNotes, deliveryNote];
    setDeliveryNotes(newDeliveryNotes);
    localStorage.setItem('deliveryNotes', JSON.stringify(newDeliveryNotes));
    setIsModalOpen(false);
  };
  
  // Generate PDF for a delivery note
  const handleGeneratePDF = (deliveryNote: any) => {
    generateDeliveryNotePDF(deliveryNote);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FileText className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Remitos
        </h2>
        <Button
          variant="primary"
          icon={<Plus className="h-5 w-5" />}
          onClick={() => setIsModalOpen(true)}
        >
          Nuevo Remito
        </Button>
      </div>
      
      {/* Search and filters */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Input
              label="Buscar"
              id="searchQuery"
              name="searchQuery"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por número, cliente o transportista..."
            />
            <div className="absolute right-3 top-9">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Delivery Notes List */}
      <Card>
        <CardHeader title={`Remitos (${filteredDeliveryNotes.length})`} />
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : filteredDeliveryNotes.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No se encontraron remitos con los criterios de búsqueda' : 'No hay remitos registrados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Número
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Transportista
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredDeliveryNotes.map((note) => (
                    <tr key={note.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {note.number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatArgentinaDate(note.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {note.clientName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {note.carrierName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Download className="h-4 w-4" />}
                            onClick={() => handleGeneratePDF(note)}
                          >
                            PDF
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Delivery Note Modal */}
      <DeliveryNoteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveDeliveryNote}
        clients={clients}
      />
    </div>
  );
};

export default Remitos;